<template>
  <Modal
    class="spaceModal"
    :isOpen="isOpen"
    :buttons="{
      column: false,
      data: [
        {
          label: space ? 'Annuler les modifications' : 'Annuler la création',
          class: 'secondary',
        },
        {
          label: space
            ? 'Enregistrer les modifications'
            : 'Confirmer la création de l\'espace',
          class: 'green',
          disabled:
            state.space?.name?.length < 2 || state.space?.name?.length > 50,
        },
      ],
    }"
    :title="space?.name || 'Créer un nouvel espace'"
    @close="close()"
    @callbackBtn1="close()"
    @callbackBtn2="save()"
  >
    <template #extra>
      <h4>Informations de l'espace</h4>
      <VForm :displayErrors="false" :callback="save">
        <template #form-body>
          <VField
            label="Nom de l'espace"
            placeholder="Mon espace"
            name="space-name"
            v-model="state.space.name"
            rules="required|max:50|min:2"
            :displayError="true"
          />
          <div class="input-wrapper">
            <label for="space-logo">Image de l'espace</label>
            <Dropload
              v-model="state.space.image_file"
              @update:modelValue="(event) => previewImage(event)"
            />
            <small> Le fichier choisi doit être une image carrée. </small>
            <transition name="fade" mode="out-in">
              <p
                class="bold logo-preview"
                v-if="space || state.space.image_file"
              >
                Aperçu:
                <img :src="imageSrc" ref="spaceImage" alt="Logo preview" />
              </p>
            </transition>
          </div>
        </template>
      </VForm>
    </template>
  </Modal>
</template>

<script setup>
import { ref, reactive, watch, computed } from "vue";
import { getImage, getRandomSpacePicture } from "@/composables/getImage.js";
import { useImageStore } from "@/stores/image.js";
import { useCompanyStore } from "@/stores/company.js";
import { useSpaceStore } from "@/stores/space.js";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

const toast = useToast();

const route = useRoute();

const { uploadImage } = useImageStore();

const { currentCompany } = useCompanyStore();

const spaceStore = useSpaceStore();

const emits = defineEmits(["close"]);

const spaceImage = ref(null);

const imageSrc = computed(() => {
  if (state.space) {
    if (state.space.image_file) return;

    if (state.space.image_id) {
      return getImage(state.space.image_id);
    }

    if (state.space.name) {
      return getRandomSpacePicture(state.space.name);
    }
  }

  return null;
});

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  space: {
    type: Object,
    required: false,
  },
  action: {
    type: String,
    required: true,
  },
});

const state = reactive({
  space: null,
});

function previewImage(image) {
  setTimeout(() => {
    spaceImage.value.src = URL.createObjectURL(image);
  }, 100);
}

async function save() {
  if (state.space.image_file) {
    const { image_id } = await uploadImage(
      currentCompany.company_id,
      "workspace_image",
      state.space.image_file
    );

    if (image_id) {
      state.space.image_id = image_id;
    } else {
      toast.error("Une erreur est survenue lors du téléchargement de l'image");
    }
  }

  if (props.action === "create") {
    const resp = await spaceStore.createSpace(
      state.space,
      route.params.companyId
    );

    if (!resp.code) {
      toast.success("Nouvel espace de travail créé avec succès");
    } else {
      toast.error(
        "Un problème est survenus durant la création de l'espace de travail, veuillez ressayer plus tard."
      );
    }
  } else {
    const { workspace } = await spaceStore.updateSpace(
      state.space,
      route.params.companyId,
      props.space.workspace_id
    );

    if (workspace.workspace_id) {
      toast.success("Espace de travail modifié avec succès");
    } else {
      toast.error(
        "Un problème est survenus durant la modification de l'espace de travail, veuillez ressayer plus tard."
      );
    }
  }

  close();
}

function close() {
  state.space = null;
  emits("close");
}

watch(props, () => {
  state.space = { ...props.space, name: props.space?.name, image_file: null };
});
</script>
